/**
 * DocViewEditorPanel — the WYSIWYG builder for an in-document ```view block.
 *
 * Rendered by the DOCUMENT host (MarkdownEditor), NOT inside the embed's CM6
 * widget: a builder edit rewrites the block text, which rebuilds/replaces the
 * widget (its React root is torn down) — a builder living inside that root would
 * close on its own first keystroke. Rendered by the stable host, it survives
 * every rebuild while the embedded view refreshes live beside it.
 *
 * It reuses the shared {@link ViewClauseComposer} (same sections as the HUD
 * builder), backed by {@link useDocViewDraft} (rows ⇄ block text). The selector
 * vocabulary comes from the workbench's compiled model via the host context.
 */
import { Wand2, X } from "lucide-react";
import { IconButton } from "@archlang/ui";
import type { ReactNode } from "react";
import { ViewClauseComposer } from "./ViewBuilder.js";
import { useDocViewDraft } from "./useDocViewDraft.js";
import { useSelectorVocabulary } from "./useSelectorVocabulary.js";

export interface DocViewEditorPanelProps {
  /** The block's current `view X { … }` text (seed only; the panel's own writes
   *  are the source of truth thereafter — see useDocViewDraft). */
  readonly blockText: string;
  /** Write new block text back (a CM6 transaction on the fenced body). */
  readonly onWrite: (text: string) => void;
  readonly onClose: () => void;
}

export function DocViewEditorPanel({ blockText, onWrite, onClose }: DocViewEditorPanelProps): ReactNode {
  const vocab = useSelectorVocabulary();
  const draft = useDocViewDraft(blockText, onWrite);
  return (
    <div
      role="dialog"
      aria-label="Edit embedded view"
      className="fixed right-4 top-24 z-overlay flex max-h-[70vh] w-96 flex-col rounded-lg border border-slate-700 bg-slate-900/95 shadow-xl backdrop-blur-md outline-none"
    >
      <div className="flex items-center gap-2 border-b border-slate-800 px-2.5 py-2">
        <Wand2 width={13} height={13} aria-hidden="true" className="shrink-0 text-sky-400" />
        <span className="text-xs uppercase tracking-wide text-slate-400">Edit view</span>
        <IconButton
          size="sm"
          aria-label="Close view builder"
          className="ml-auto"
          onClick={onClose}
        >
          <X size={14} />
        </IconButton>
      </div>
      {/* Edits write straight into the block as you compose (live) — no Apply,
          no save-to-model chrome: the doc IS the storage. The name is a plain
          editable field (the block always owns its own view name). */}
      <div className="space-y-2 overflow-auto p-2.5">
        <input
          aria-label="View name"
          className="w-full rounded-sm border border-slate-700 bg-slate-800 px-2 py-1 text-sm text-slate-200 focus:border-slate-500 focus:outline-hidden"
          placeholder="View name"
          value={draft.name}
          onChange={(e) => draft.onName(e.target.value)}
        />
        <ViewClauseComposer
          rows={draft.rows}
          onChangeRows={draft.onChangeRows}
          errors={draft.errors}
          vocab={vocab}
        />
      </div>
    </div>
  );
}
